import React, {useCallback, useState} from 'react';
import Link from 'next/link';
import {CaseStatuses, DEFAULT_SESSION_DURATION_IN_MINUTES} from '../../constants';
import {UserAvatar} from '@/components/UserAvatar';
import {Typography} from '@/components/ui/typography';
import {cn} from '@/lib/utils';
import {useSelector} from 'react-redux';
import {selectUserById} from '@/features/usersSlice';
import {Button} from '@/components/ui/button';
import {Users, CalendarClock, CalendarPlus, Clock4} from 'lucide-react';
import {selectCaseById} from '@/features/casesSlice';
import RegisterButton from '@/components/RegisterButton';
import {useAuth} from '@/util/auth';
import {formatDate, formatName} from '@/util/format';
import {selectTimezone} from '@/features/timezoneSlice';

const CaseCardHorizontal = ({caseId, onParticipantsClick, className, onRegisterClick, onUnregisterClick}: {
    caseId: string;
    onParticipantsClick?: () => void;
    onRegisterClick?: (caseId: string) => void;
    onUnregisterClick?: (caseId: string) => void;
    className?: string;
}) => {
    const currentCase = useSelector((state) => selectCaseById(state, caseId));
    const state = currentCase?.state;
    const isPlaceholder = state === CaseStatuses.TENTATIVE;
    const title = currentCase?.title;
    const userId = currentCase?.user;
    const user = useSelector(state => userId ? selectUserById(state, userId) : null);

    const auth = useAuth();
    const [isLoginOverlayVisible, setIsLoginOverlayVisible] = useState(false);
    const isOpenCase = (!auth.isAuthenticated && currentCase?.public) || auth.isAuthenticated;
    const onFocus = useCallback(() => {
        if (!isOpenCase) {
            setIsLoginOverlayVisible(true);
        }
    }, [setIsLoginOverlayVisible, isOpenCase]);

    const onBlur = useCallback(() => {
        setIsLoginOverlayVisible(false);
    }, [setIsLoginOverlayVisible, isOpenCase]);
    const timezone = useSelector(selectTimezone);

    if (!currentCase) {
        return null;
    }

    return (
        <div className={cn("w-full min-h-[160px] shadow-lg grid grid-cols-12 border border-slate-300 bg-background rounded-xl overflow-hidden", className)}>
            <div className={cn("col-span-12 lg:col-span-3 bg-cardFooter p-4", {
                "bg-slate-300": isPlaceholder,
            })}>
                <div className="flex gap-2 items-center">
                    <UserAvatar userId={userId} />
                    {!!user ? (
                        <div>
                            <p className="text-sm font-medium leading-none">
                                {formatName(user)}
                            </p>
                            <p className="text-sm text-muted-foreground">{user.position}</p>
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="col-span-12 lg:col-span-9 p-4 flex gap-2 flex-col justify-between">
                <div className="flex flex-wrap gap-2 justify-between relative">
                    <div className={cn('absolute bg-background inset-0 animate-in fade-in-0 pointer-events-none', {
                        'opacity-0': !isLoginOverlayVisible,
                    })}>
                        <p>Become a Casehub member to see more. <strong>Free trial for 14 days</strong>, cancel any time.</p>
                    </div>
                    {isPlaceholder ? (
                        <Typography className="decoration-0 font-semibold text-lg line-clamp-2" variant="large">
                            {title}
                        </Typography>
                    ) : (
                        <Link href={`/case/${caseId}`} passHref>
                            <Typography className="decoration-0 font-semibold text-lg line-clamp-2" variant="large" asChild>
                                <a>{title}</a>
                            </Typography>
                        </Link>
                    )}
                    <div className="flex flex-shrink-0 gap-2">
                        <Button variant="plain" size="subtle" className="text-muted-foreground" onClick={onParticipantsClick}>
                            <Users className="w-4 h-4" />
                            {currentCase.register_count} / {currentCase.register_max}
                        </Button>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap justify-between">
                    <div className="flex gap-4">
                        <div className="flex gap-x-2 text-xs text-muted-foreground items-center">
                            <CalendarClock className="w-4 h-4" /> {formatDate({ts: currentCase.scheduled_to, format: 'long', tz: timezone})}
                        </div>
                        <div className="flex gap-x-2 text-xs text-muted-foreground items-center">
                            <Clock4 className="w-4 h-4" /> {currentCase.duration || DEFAULT_SESSION_DURATION_IN_MINUTES} min
                        </div>
                    </div>

                    <div onMouseEnter={onFocus} onMouseLeave={onBlur}>
                        <RegisterButton
                            caseId={currentCase.id}
                            onRegisterClick={onRegisterClick}
                            onUnregisterClick={onUnregisterClick}
                            textInterested={isLoginOverlayVisible ? 'Become a member' : undefined}
                            textJoin={isLoginOverlayVisible ? 'Become a member' : undefined}
                            textRegister={isLoginOverlayVisible ? 'Become a member' : undefined}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
};

export default CaseCardHorizontal;
